/**
 * AI CONTEXT
 * Purpose: static label showing the project name + a student's projected project day vs. total days.
 * Renders as "{project.name} ({projectedDay}/{daysToComplete})" inside AttendanceRow's subtitle slot.
 * Used by: AttendanceViewStudentListSection (exceptions) + TeacherAttendanceSection (exceptions).
 */
import { projectAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { AttendanceStatus } from "#customTypes/types"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { getProjectedProjectDay } from "#utils/studentHelpers"

const StudentProjectDaySubtitle = ({
  student,
  session,
  status,
}: {
  student: StudentConstructor
  session: ClassSessionConstructor
  status: AttendanceStatus
}) => {
  const project = useOneDoc(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    session.projectId ?? undefined
  )

  if (!project) return null

  const rawProjectedDay = getProjectedProjectDay(
    student,
    session.projectId,
    status
  )
  const rawDaysToComplete = parseInt(project.daysToComplete)

  const projectedDay = Number.isFinite(rawProjectedDay) ? rawProjectedDay : 0
  const daysToComplete = Number.isFinite(rawDaysToComplete)
    ? rawDaysToComplete
    : 0

  return (
    <Flex
      gap={4}
      style={{
        alignItems: "center",
        justifyContent: "flex-start",
        flexWrap: "nowrap",
        minWidth: 0,
        width: "100%",
      }}
    >
      <TextOverrun text={project.name} />
      <span style={{ flexShrink: 0 }}>
        ({projectedDay}/{daysToComplete})
      </span>
    </Flex>
  )
}

export default StudentProjectDaySubtitle
